import React from "react";
import { useParams, Link } from "react-router-dom";
import { Mail, Phone, MapPin, MessageCircle, ArrowLeft } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";
import { useContent } from "../lib/ContentProvider";

const LABELS = {
  imprint: "Imprint",
  privacy: "Privacy",
  terms: "Terms",
  about: "About",
};

export default function Legal({ slug: slugProp }) {
  const { slug: slugParam } = useParams();
  const slug = slugProp || slugParam;
  const { locale } = useI18n();
  const { content } = useContent();
  const cms = content?.legal?.[slug]?.[locale] || content?.legal?.[slug]?.en;
  const label = cms?.title || LABELS[slug] || "Legal";
  const eyebrow = slug === "about" ? "About" : "Legal";

  return (
    <div data-testid={`legal-page-${slug || "index"}`}>
      <section className="py-24 sm:py-28 border-b border-zinc-200 relative overflow-hidden">
        <div className="absolute inset-0 technical-grid pointer-events-none" />
        <div className="container-oj relative">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-oj-ink">
            <ArrowLeft className="size-4" /> Home
          </Link>
          <div className="mt-6 text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">{eyebrow}</div>
          <h1 className="font-display mt-3 text-balance text-5xl sm:text-6xl font-black tracking-tighter leading-[0.95] text-oj-ink">
            {label}
          </h1>
          {slug === "about" && (
            <p className="mt-4 max-w-2xl text-zinc-600">
              For the product story — why operators switch — see{" "}
              <Link to="/why" className="font-semibold text-oj-ink underline underline-offset-4">
                Why O&#39;javana
              </Link>
              .
            </p>
          )}
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white">
        <div className="container-oj">
          {slug === "imprint" ? <Imprint /> : <LegalBody label={label} body={cms?.body} />}
        </div>
      </section>
    </div>
  );
}

function LegalBody({ label, body }) {
  if (!body) {
    return (
      <div className="max-w-2xl text-zinc-600">
        <p>The {label.toLowerCase()} document is being finalised. For any urgent question please reach us at <a className="text-oj-ink font-semibold underline underline-offset-4" href="mailto:support@ojavana.com">support@ojavana.com</a>.</p>
      </div>
    );
  }
  return (
    <div className="max-w-3xl text-zinc-700 text-[15px] leading-relaxed whitespace-pre-wrap" data-testid="legal-cms-body">
      {body}
    </div>
  );
}

function Imprint() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="imprint-content">
      <div className="lg:col-span-7 space-y-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Responsible for content</div>
          <div className="mt-4 space-y-1 text-zinc-800">
            <Row label="UID" value="CHE-153.649.731" />
            <Row label="CH-ID" value="CH-645-4123036-5" />
          </div>
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Address</div>
          <address className="mt-4 not-italic text-oj-ink text-lg leading-relaxed">
            Rue des Saars 115<br />
            2000 Neuchâtel<br />
            Switzerland
          </address>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Contact icon={Phone} label="Phone" value="+41 32 510 06 20" href="tel:+41325100620" />
          <Contact icon={MessageCircle} label="WhatsApp" value="+41 79 315 06 00" href="https://wa.me/41793150600" />
          <Contact icon={Mail} label="Email" value="support@ojavana.com" href="mailto:support@ojavana.com" />
          <Contact icon={MapPin} label="Headquarters" value="Neuchâtel · Switzerland" />
        </div>
      </div>

      <aside className="lg:col-span-5">
        <div className="rounded-2xl border border-zinc-200 p-7 bg-zinc-50">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">/headquarters</div>
          <div className="mt-3 font-display text-2xl font-bold text-oj-ink leading-tight">
            Built in Neuchâtel for the Swiss market.
          </div>
          <p className="mt-3 text-sm text-zinc-700">
            O&#39;javana is operated from Rue des Saars 115, on the shore of Lake Neuchâtel.
            Hardware, software and support all come from the same building.
          </p>
          <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-md bg-white border border-zinc-200 p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Office</div>
              <div className="mt-1 font-semibold text-oj-ink">08:00 – 19:00 CET</div>
            </div>
            <div className="rounded-md bg-white border border-zinc-200 p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Languages</div>
              <div className="mt-1 font-semibold text-oj-ink">FR · DE · EN · IT</div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-3">
      <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 w-20 pt-1">{label}</span>
      <span className="font-mono text-oj-ink">{value}</span>
    </div>
  );
}

function Contact({ icon: Icon, label, value, href }) {
  const inner = (
    <>
      <Icon className="size-4 text-oj-brand mt-0.5 shrink-0" />
      <span className="min-w-0">
        <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
        <span className="block font-semibold text-oj-ink break-words">{value}</span>
      </span>
    </>
  );
  if (href)
    return (
      <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="flex items-start gap-3 rounded-md border border-zinc-200 p-3 hover:border-zinc-900 transition-colors">
        {inner}
      </a>
    );
  return <div className="flex items-start gap-3 rounded-md border border-zinc-200 p-3">{inner}</div>;
}
